import type { BoxProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { Divider, ProductLineItem } from '@time-line/components/common';
import {
  hasSellingPlan,
  hasServings,
} from 'components/cart/utils/typeInference';
import { useProductsUpdatedPrices } from 'hooks/product/useProductsUpdatedPrices';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductOverrides } from 'types/cms/blocks/ProductBlock';
import { PurchaseType } from 'types/product/PurchaseType';
import type { Variant } from 'types/product/Variant';
import { getPricePerMonth } from 'utils/getPricePerMonth';
import { getSortedItems } from 'utils/getSortedItems';
import { resolveProductInfo } from 'utils/product/resolveProductInfo';

import { SHOP_NOW_CTA_LABEL, SUBSCRIBE_AND_SAVE_CTA_LABEL } from './constants';
import { CtaButton } from './CtaButton';
import { StickyCta } from './StickyCta';

export interface ProductCtasProps extends BoxProps {
  blockId: string;
  promoCodes?: PromoCode[];
  purchaseType?: PurchaseType;
  isDiscountHidden?: boolean;
  productOverrides?: ProductOverrides;
  product: {
    slug: string;
    variants?: {
      gid: string;
    }[];
  };
  colorScheme: 'white' | 'gray';
}

const getPrice = (item: Variant, purchaseType?: PurchaseType) => {
  if (purchaseType === PurchaseType.OTP) {
    return item.price.amount;
  }

  /* purchaseType is SUB or null here */
  if (hasServings(item)) {
    return getPricePerMonth(item.servings, item.price.amount);
  }

  return item.price.amount;
};

export function ProductCtas({
  blockId,
  promoCodes,
  purchaseType,
  isDiscountHidden,
  productOverrides,
  product,
  colorScheme,
  ...props
}: ProductCtasProps) {
  const { slug, variants } = product;

  const {
    value: { countryCode, currencyCode },
  } = useLocalStorage(UserLocale);
  const sanityProduct = useSanityProduct(slug);
  const { data: shopifyProduct } = useShopifyProduct(slug);

  const { data: updatedProducts } = useProductsUpdatedPrices({
    blockId,
    shopifyProducts: shopifyProduct ? [shopifyProduct] : null,
    promoCodes,
  });

  const updatedProduct = updatedProducts?.[0];

  if (!sanityProduct || !shopifyProduct || !updatedProduct) return null;

  const sortedItems = getSortedItems(
    updatedProduct,
    variants?.map((v) => v.gid),
    purchaseType,
  );

  const image = shopifyProduct.variants[0].imageUrl;

  const cheapestItem = sortedItems[0];

  if (!cheapestItem) return null;

  const price = getPrice(cheapestItem, purchaseType);
  const compareAtPrice =
    !isDiscountHidden && purchaseType === PurchaseType.OTP
      ? cheapestItem.compareAtPrice?.amount
      : undefined;

  const productInfo = resolveProductInfo({
    sanityProduct,
    productOverrides,
    variants: sortedItems,
    selectedVariantsIds: variants?.map((v) => v.gid),
  });
  const beforeText = sortedItems.length > 1 ? 'From ' : undefined;

  const availableForSale = sortedItems?.every(
    (variant) => variant.availableForSale,
  );

  const isSubscription =
    purchaseType !== PurchaseType.OTP && hasSellingPlan(cheapestItem);

  return (
    <Flex className="hero_product-ctas" direction="column" w="full" {...props}>
      <Divider
        w="full"
        color={colorScheme === 'white' ? 'whiteAlpha.600' : 'blackAlpha.600'}
      />
      <ProductLineItem
        availableForSale={availableForSale}
        colorScheme={colorScheme}
        title={productInfo.title}
        subTitle={productInfo?.subtitle}
        image={image}
        price={price}
        countryCode={countryCode}
        currencyCode={currencyCode}
        compareAtPrice={compareAtPrice}
        beforeText={beforeText}
        afterText={isSubscription ? '/mo' : undefined}
      />
      <CtaButton anchorId={blockId} size="md" colorScheme={colorScheme}>
        {isSubscription ? SUBSCRIBE_AND_SAVE_CTA_LABEL : SHOP_NOW_CTA_LABEL}
      </CtaButton>
      <StickyCta
        type="anchor"
        anchorId={blockId}
        availableForSale={availableForSale}
        isSubscription={isSubscription}
      />
    </Flex>
  );
}
